import type { ApiClient } from '@carbonwebmail/api-client'
import type { OrgRole, Resource } from '@carbonwebmail/domain'
import { may } from '@carbonwebmail/domain'
import type { ReactNode } from 'react'
import { Link, NavLink } from 'react-router'
import { CarbonMark } from './CarbonMark.js'
import { count } from './format.js'
import type { IconName } from './Icon.js'
import { Icon } from './Icon.js'
import { SearchBar } from './SearchBar.js'
import { StatusPill } from './StatusPill.js'
import { YourRoleProvider } from './yourRole.js'

/**
 * The frame every signed-in screen sits in.
 *
 * The navigation lists only what is built. The design has six groups and
 * fifteen destinations; adding the rest now would be a menu of dead ends, and a
 * product that offers places you cannot go teaches people not to trust the
 * menu. Entries arrive with their screens.
 *
 * Two things here are real data rather than decoration. The badge beside the
 * sending monitor is the count of sends that came back **unknown** — the number
 * this product exists to make impossible to ignore, so it follows you around
 * the application rather than waiting on one screen. And the banner appears
 * only when a bounce rate is genuinely near the level at which mailbox
 * providers suspend a sending domain.
 */

export interface AppShellProps {
  /**
   * Handed to the search bar, and to nothing else.
   *
   * The shell is a view and holds no state; this is the one component in it
   * that asks the server anything, and it does so through its own view model.
   */
  readonly client: ApiClient
  readonly organizationName: string
  readonly email: string
  readonly onSignOut: () => void
  /** Adds the operator group. Courtesy only — the server decides again. */
  /**
   * Whether to show the entries only an administrator can open.
   *
   * A courtesy, not a control: every one of those routes refuses on the server,
   * which is the only place a refusal means anything. Showing them to a member
   * would be offering a door that opens onto a message about not being allowed.
   */
  /** Decides which entries appear, through the same table the server uses. */
  readonly yourRole: OrgRole
  readonly theme: 'light' | 'dark'
  readonly onToggleTheme: () => void
  /**
   * Sends awaiting reconciliation, or undefined while unknown.
   *
   * Undefined is not zero. Zero says "nothing needs you", and saying that
   * before the answer has arrived is the kind of small lie that stops people
   * believing the number at all.
   */
  readonly unknownSends: number | undefined
  /** Shown only when it is close enough to the suspension threshold to matter. */
  readonly bounceWarning: { readonly rate: string; readonly limit: string } | undefined
  readonly children: ReactNode
}

/**
 * The fourth element is the permission the entry needs, if it needs one.
 *
 * **It was `adminOnly?: true` and no entry ever set it**, so the filter below
 * it had been doing nothing since the settings screens became one board. A
 * flag nothing sets reads as a working control, which is worse than no control
 * at all — it is why a viewer would have been offered Compose.
 */
type NavItem = readonly [route: string, label: string, icon: IconName, needs?: Resource]
type NavGroup = readonly [string, readonly NavItem[]]

const NAV: readonly NavGroup[] = [
  ['Overview', [['/dashboard', 'Sending health', 'dashboard']]],
  [
    'Audience',
    [
      // Fields, Duplicates and Import are tabs on the contacts board now, not
      // screens of their own: they are things somebody does to an audience
      // while looking at it, and a sidebar entry each put every one of them
      // behind leaving the list.
      ['/contacts', 'Contacts', 'contacts'],
      ['/segments', 'Segments', 'segments'],
    ],
  ],
  [
    'Campaigns',
    [
      ['/campaigns', 'Campaigns', 'campaigns'],
      // Its own entry, and deliberately above the library it saves into:
      // composing is what somebody comes here to do, and it was reachable only
      // from inside a campaign or a template — which reads as not existing.
      // Writes a template, so a viewer is not offered it.
      ['/compose/template/new', 'Compose', 'compose', 'templates'],
      ['/templates', 'Templates', 'templates'],
      ['/designer', 'Design with AI', 'sparkle', 'templates'],
    ],
  ],
  [
    'Deliverability',
    [
      // Sending domains is under Settings now. What belongs here is what you
      // watch; a domain is set up once and then forgotten.
      ['/sends', 'Sending monitor', 'monitor'],
      ['/suppressions', 'Suppression list', 'suppression'],
      ['/reputation', 'Reputation', 'reputation'],
    ],
  ],
  ['Compliance', [['/consent', 'Consent record', 'consent']]],
  /*
   * One entry, not six. The profile, the organisation, the team, the provider,
   * the domains and the keys are tabs on one board — `SettingsBoard` says why —
   * and this was the longest group in the sidebar for the screens people open
   * least often.
   *
   * No permission needed: the profile is every member's, and the board leaves
   * out the tabs they cannot open.
   */
  /*
   * Support sits with the account rather than in the settings board.
   *
   * Asking for help is not configuration, and burying it a tab deep inside
   * Settings is how a product ends up with customers emailing instead.
   */
  [
    'Account',
    [
      ['/settings', 'Settings', 'settings'],
      ['/support', 'Support', 'consent'],
    ],
  ],
]

export function AppShell({
  client,
  organizationName,
  email,
  onSignOut,
  yourRole,
  theme,
  onToggleTheme,
  unknownSends,
  bounceWarning,
  children,
}: AppShellProps) {
  /*
   * The operator panel is a different application on a different host with
   * different accounts, so there is nothing here to link to and nobody signed
   * in here who could use it.
   *
   * An entry is shown when its resource is writable, because the only entries
   * that declare one are the two that exist to create something. Everything
   * else is a screen to look at, and every role may look.
   *
   * A group whose every entry was filtered away is dropped with it, rather
   * than leaving a heading over nothing.
   */
  const groups = NAV.flatMap<NavGroup>(([heading, items]) => {
    const visible = items.filter(
      ([, , , needs]) => needs === undefined || may(yourRole, needs, 'write'),
    )
    return visible.length === 0 ? [] : [[heading, visible]]
  })

  return (
    /*
      The frame is the viewport, and the content scrolls inside it.

      It used to be `min-h-screen` with the document scrolling and the sidebar
      pinned by `sticky`. That works for a page of rows, and makes a screen that
      wants to *fill* the window impossible: `flex-1` on a container with no
      definite height grows to its content rather than to what is left, so the
      designer could only guess at a height and guessed 560px. A fixed frame
      gives every screen a real one to fill, and `flex-1` under `main` now means
      "the rest of the window".
    */
    <div className="flex h-screen overflow-hidden bg-bg">
      <aside className="flex h-full w-[248px] flex-none flex-col border-line border-r bg-surface-2">
        <div className="flex h-14 flex-none items-center gap-2.5 px-4">
          <CarbonMark className="h-[26px] w-[22px] flex-none" />
          <span className="truncate font-bold text-[15px] text-t1 -tracking-[0.01em]">
            Carbon Mail
          </span>
        </div>

        {/*
          Which organisation, and which account. Not a switcher: nobody belongs
          to two organisations yet, and a control that opens a list of one is a
          promise about a feature that does not exist.
        */}
        <div className="mx-3 mb-2 flex items-center gap-2.5 rounded-card border border-line bg-surface p-2.5">
          <span className="flex h-8 w-8 flex-none items-center justify-center rounded-field bg-brand font-bold text-[12px] text-brand-contrast">
            {initials(organizationName)}
          </span>
          <span className="min-w-0">
            <span className="block truncate font-semibold text-[13px] text-t1">
              {organizationName}
            </span>
            <span className="block truncate text-[11px] text-t3">{email}</span>
          </span>
        </div>

        {/*
          A button rather than a nav entry, because writing a campaign is an
          action and the list beneath it is a set of places.

          It is here at all because it was not anywhere: the only way to start
          writing was to open Campaigns and find a button on that page, and a
          product whose main verb is two screens deep reads as one that cannot
          do it. The same words as the button on the campaign list, so one
          destination has one name.
        */}
        <Link
          to="/campaigns/new"
          className="mx-3 mb-3 flex items-center justify-center gap-2 rounded-pill bg-brand px-3 py-2 font-bold text-[13px] text-brand-contrast hover:bg-brand-strong"
        >
          <Icon name="compose" />
          New campaign
        </Link>

        <nav className="flex-1 overflow-y-auto px-2.5 py-2">
          {groups.map(([group, items]) => (
            <div className="mb-4" key={group}>
              <p className="mb-1 px-2 font-semibold text-[11px] text-t3 uppercase tracking-[0.08em]">
                {group}
              </p>
              {items.map(([to, label, icon]) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-field px-2 py-[7px] font-medium text-[13px] ${
                      isActive
                        ? 'bg-brand-soft text-brand-strong'
                        : 'text-t2 hover:bg-line-2 hover:text-t1'
                    }`
                  }
                >
                  <Icon name={icon} />
                  <span className="flex-1 truncate">{label}</span>
                  {to === '/sends' && unknownSends !== undefined && unknownSends > 0 ? (
                    <StatusPill tone="unknown">{count(unknownSends)}</StatusPill>
                  ) : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="flex-none border-line border-t p-3">
          <button
            type="button"
            onClick={onToggleTheme}
            className="flex w-full items-center gap-2 rounded-field px-2 py-1.5 font-medium text-[12px] text-t2 hover:bg-line-2 hover:text-t1"
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
            {/* Says what pressing it does, not what is currently true. A button
                labelled with its own state reads as a broken toggle. */}
            {theme === 'dark' ? 'Light' : 'Dark'}
          </button>
          <button
            type="button"
            onClick={onSignOut}
            className="mt-0.5 w-full rounded-field px-2 py-1.5 text-left font-medium text-[12px] text-t2 hover:bg-line-2 hover:text-t1"
          >
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/*
          The search bar sits above the warning banner rather than below it: the
          banner is occasional and the search is always there, and a control
          that moves down the page when something goes wrong is one people stop
          reaching for without looking.
        */}
        <div className="flex h-14 flex-none items-center border-line border-b px-8">
          <SearchBar client={client} />
        </div>

        {bounceWarning === undefined ? null : (
          <div
            role="status"
            className="flex flex-wrap items-center gap-x-2 gap-y-1 border-warn/40 border-b bg-warn-soft px-8 py-2.5 text-[13px] text-t1"
          >
            <strong className="font-semibold">Bounce rate {bounceWarning.rate}</strong>
            <span className="text-t2">
              over the last 7 days. Mailbox providers suspend sending domains above{' '}
              {bounceWarning.limit} — nothing here pauses automatically.
            </span>
            <NavLink className="font-semibold text-brand" to="/suppressions">
              See what bounced
            </NavLink>
          </div>
        )}

        {/*
          A flex column, so a screen that asks for `flex-1` fills exactly what
          is left of the window — padding included, without subtracting the
          search bar and the banner by hand. Everything else stacks and scrolls
          as it did.
        */}
        {/*
          Every signed-in screen is below here, which is why the role is
          published from this point: the sidebar above already decides what
          exists using it, and the screens below decide what is offered.
        */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto px-8 py-7">
          <YourRoleProvider yourRole={yourRole}>{children}</YourRoleProvider>
        </main>
      </div>
    </div>
  )
}

/**
 * Up to two letters from the organisation's name.
 *
 * From the words rather than the first two characters, so "Northwind Trading"
 * is NT and not NO.
 */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const letters = words.slice(0, 2).map((word) => word[0] ?? '')
  return (letters.join('') || '?').toUpperCase()
}
